// Today's count and writing sessions on this browser (the Mac's WritingSession, LibraryModel+Sprints): per device, never synced.
// The code says "session" for the day's running count and "sprint" for the timed stretch of writing; the writer reads "today"
// and "session" (renamed 2026-10-13), as the Mac does.
//
// A session is per project and day (localStorage `ws:writingSession-<project>`): the manuscript's words when it began
// (the net figure is today's total less that), and the words written and cut in the counted text since, save by save
// (`wordsSaved`), kept apart. A new one starts each day, or when the writer asks.
//
// A sprint is minutes and an optional word goal (`ws:activeSprint`, so a reload picks it up, and one that ended
// meanwhile is filed); it counts each save's written and cut words as the session does. Finished sprints go in the log
// (`ws:sprintLog`, newest first, 500 kept). Live figures add the typing not yet saved (`LiveProject.unsavedChange`).
import { storageId } from './storageId.ts';
import { onNewPostWords } from './newPostCaret.ts';
import { addToLog, canExtend, extendedSprint, isOver, newSprint, plannedEnd, recordSprint, removeFromLog, type WritingSprint } from '@writing-suite/core';

export interface WritingSession {
  projectID: string;
  /** yyyy-MM-dd, this browser's day. */
  day: string;
  started: number;
  /** Manuscript words when it began. */
  baseline: number;
  added: number;
  removed: number;
}

const SESSION = (id: string) => `ws:writingSession-${storageId(id)}`;
const ACTIVE = 'ws:activeSprint';
const LOG = 'ws:sprintLog';

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // this page only
  }
}

/** `WritingDay.key` in this browser's zone. */
export function today(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

const isSprint = (v: unknown): v is WritingSprint =>
  !!v && typeof v === 'object' && typeof (v as WritingSprint).id === 'string' && typeof (v as WritingSprint).started === 'number' && typeof (v as WritingSprint).duration === 'number';

export function readSprintLog(): WritingSprint[] {
  const log = read<unknown[]>(LOG);
  return Array.isArray(log) ? log.filter(isSprint) : [];
}

/** A test's short sprint: `localStorage['ws:sprint-seconds']` overrides the length (end-to-end tests only). */
function testSeconds(): number | undefined {
  const s = Number(read<number>('ws:sprint-seconds'));
  return Number.isFinite(s) && s > 0 ? s : undefined;
}

export interface WritingState {
  session: WritingSession | null;
  sprint: WritingSprint | null;
  /** The sprint just finished, for its summary (until dismissed). */
  finished: WritingSprint | null;
  log: WritingSprint[];
}

/** One project's session and this browser's sprint. */
export class Writing {
  state: WritingState;
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;

  private stopPassing: () => void;

  constructor(
    readonly projectId: string,
    private readonly o: {
      projectTitle: () => string;
      /** Manuscript words now (saved), for a new session's baseline. */
      words: () => number;
      /** Saves what's typed now (so a sprint counts only what comes after, and all of what came before its end). */
      flush: () => Promise<void>;
      now?: () => number;
    },
  ) {
    const active = read<unknown>(ACTIVE);
    this.state = { session: null, sprint: isSprint(active) ? active : null, finished: null, log: readSprintLog() };
    this.ensureSession();
    // A new post's copied opening and sign-off aren't words written today.
    this.stopPassing = onNewPostWords((project, words) => { if (project === this.projectId) this.passOver(words); });
    if (this.state.sprint) {
      const s = this.state.sprint;
      if (isOver(s, this.now())) void this.finish(plannedEnd(s));
      else this.schedule();
    }
  }

  private now() {
    return this.o.now?.() ?? Date.now();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private set(change: Partial<WritingState>) {
    this.state = { ...this.state, ...change };
    for (const fn of this.listeners) fn();
  }

  dispose() {
    this.stopPassing();
    if (this.timer) clearTimeout(this.timer);
    this.listeners.clear();
  }

  // MARK: Sessions

  /** Today's session for this project, picked up or started (`ensureWritingSession`). */
  ensureSession() {
    const day = today(new Date(this.now()));
    if (this.state.session?.day === day) return;
    const saved = read<WritingSession>(SESSION(this.projectId));
    if (saved && saved.day === day && typeof saved.baseline === 'number') {
      this.set({ session: { ...saved, added: saved.added ?? 0, removed: saved.removed ?? 0 } });
    } else this.newSession();
  }

  /** New Session: from the manuscript as it is now. */
  newSession() {
    const now = this.now();
    const session: WritingSession = { projectID: this.projectId, day: today(new Date(now)), started: now, baseline: this.o.words(), added: 0, removed: 0 };
    write(SESSION(this.projectId), session);
    this.set({ session });
  }

  /** Words added to the manuscript that weren't written (a new post's opening and sign-off): the day's count leaves them out. */
  passOver(words: number) {
    this.ensureSession();
    const s = this.state.session;
    if (!s || !words) return;
    const session = { ...s, baseline: s.baseline + words };
    write(SESSION(this.projectId), session);
    this.set({ session });
  }

  /** A save changed a counted fragment's words (`wordsSaved`): the session and a running sprint count it. */
  wordsSaved(before: number, after: number) {
    if (before === after) return;
    this.ensureSession();
    const s = this.state.session;
    if (s) {
      const session = { ...s, added: s.added + Math.max(0, after - before), removed: s.removed + Math.max(0, before - after) };
      write(SESSION(this.projectId), session);
      this.set({ session });
    }
    const sprint = this.state.sprint;
    if (sprint && sprint.projectID === this.projectId) {
      const next = recordSprint(sprint, before, after);
      write(ACTIVE, next);
      this.set({ sprint: next });
    }
  }

  // MARK: Sprints

  async start(minutes: number, goal: number | null) {
    await this.o.flush(); // so the sprint counts only what's written from now
    const sprint = newSprint({
      id: crypto.randomUUID().toLowerCase(), projectID: this.projectId, projectTitle: this.o.projectTitle(), started: this.now(), minutes, goal,
      seconds: testSeconds(),
    });
    write(ACTIVE, sprint);
    write('ws:sprintMinutes', minutes);
    write('ws:sprintGoal', goal ?? 0);
    this.set({ sprint, finished: null });
    this.schedule();
  }

  private schedule() {
    if (this.timer) clearTimeout(this.timer);
    const s = this.state.sprint;
    if (!s) return;
    this.timer = setTimeout(() => void this.finish(plannedEnd(s)), Math.max(0, plannedEnd(s) - this.now()));
  }

  /** Ends the sprint (at its time, or now if stopped early), files it and keeps it for the summary. */
  async finish(at = this.now()) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    await this.o.flush(); // the last words typed count
    const s = this.state.sprint;
    if (!s) return;
    // Another page (a second tab, a reload) may have filed or discarded it already: then there's nothing to file.
    const stored = read<unknown>(ACTIVE);
    if (!isSprint(stored) || stored.id !== s.id) {
      this.set({ sprint: null, log: readSprintLog() });
      return;
    }
    const done: WritingSprint = { ...s, ended: Math.min(at, plannedEnd(s)) };
    const log = addToLog(readSprintLog(), done);
    write(LOG, log);
    write(ACTIVE, null);
    this.set({ sprint: null, finished: done, log });
  }

  /** Runs the session that just finished for `minutes` more (`extendSprint`): taken back out of the log, its words and goal
   *  kept, and filed again when it ends. */
  extend(finished: WritingSprint, minutes: number) {
    const now = this.now();
    if (this.state.sprint || !canExtend(finished, now)) return;
    const stored = read<unknown>(ACTIVE);
    if (isSprint(stored)) return; // another tab started one meanwhile
    const log = removeFromLog(readSprintLog(), finished.id);
    const sprint = extendedSprint(finished, minutes, now);
    write(LOG, log);
    write(ACTIVE, sprint);
    this.set({ sprint, finished: null, log });
    this.schedule();
  }

  /** Stops without keeping it (a sprint started by mistake). */
  discard() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    write(ACTIVE, null);
    this.set({ sprint: null });
  }

  dismissSummary() {
    this.set({ finished: null });
  }
}

export function lastSprintChoices(): { minutes: number; goal: number } {
  const minutes = Number(read<number>('ws:sprintMinutes'));
  const goal = Number(read<number>('ws:sprintGoal'));
  return { minutes: Number.isFinite(minutes) && minutes > 0 ? minutes : 25, goal: Number.isFinite(goal) && goal > 0 ? goal : 0 };
}

/** What the footer shows while a session runs (the Mac's `sessionShowsTimeLeft`/`sessionShowsWords`): the time left, the
 *  words written. Both on until the writer turns one off, in this browser. */
export type SessionDisplay = { timeLeft: boolean; words: boolean };
const SHOWS_TIME = 'ws:sessionShowsTimeLeft';
const SHOWS_WORDS = 'ws:sessionShowsWords';
export function sessionDisplay(): SessionDisplay {
  return { timeLeft: read<boolean>(SHOWS_TIME) !== false, words: read<boolean>(SHOWS_WORDS) !== false };
}
export function setSessionDisplay(display: SessionDisplay) {
  write(SHOWS_TIME, display.timeLeft);
  write(SHOWS_WORDS, display.words);
  // The footer reads it on its next tick; nothing else keeps a copy.
}
